import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Chip, Stack,
  CircularProgress, Alert, Avatar, Divider, Tooltip,
} from '@mui/material';
import {
  ScubaDiving, Check, Close, Straighten, Schedule, Place, Thermostat, InfoOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { diveShareService, IncomingShare } from '../services/diveShareService';
import { UnitPreferences, formatDepth, formatTemperature } from '../utils/unitConversions';

interface Props {
  userId: string | null;
  unitPreferences?: UnitPreferences;
  /** Lets a parent refresh its own dive list after an accept. */
  onAccepted?: () => void;
}

const DEFAULT_UNITS: UnitPreferences = {
  depth: 'meters',
  temperature: 'celsius',
  pressure: 'bar',
};

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function durationLabel(iso: string): string {
  // Backend serializes TimeSpan as "hh:mm:ss"
  const parts = iso?.split(':');
  if (!parts || parts.length < 2) return '';
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const total = h * 60 + m;
  return `${total} min`;
}

/**
 * Dives buddies have offered. Accepting writes an independent copy into the viewer's own
 * log, so they can edit it freely afterward.
 */
const IncomingSharesPanel: React.FC<Props> = ({ userId, unitPreferences, onAccepted }) => {
  const navigate = useNavigate();
  const units = unitPreferences ?? DEFAULT_UNITS;

  const [shares, setShares] = useState<IncomingShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) { setShares([]); return; }
    setLoading(true);
    setError(null);
    try {
      setShares(await diveShareService.getIncoming());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load shared dives');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (share: IncomingShare) => {
    setBusyId(share.shareId);
    setError(null);
    try {
      const dive = await diveShareService.accept(share.shareId);
      setShares(prev => prev.filter(s => s.shareId !== share.shareId));
      onAccepted?.();
      // Straight to the dive so they can add their air and weights while it's fresh
      navigate(`/dive/${dive.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept dive');
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (share: IncomingShare) => {
    setBusyId(share.shareId);
    setError(null);
    try {
      await diveShareService.decline(share.shareId);
      setShares(prev => prev.filter(s => s.shareId !== share.shareId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to decline dive');
    } finally {
      setBusyId(null);
    }
  };

  if (!userId) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && shares.length === 0) {
    return <Alert severity="warning" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (shares.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <ScubaDiving sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          No dives shared with you
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When a buddy shares a dive, it shows up here to add to your log.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        {shares.map(share => {
          const s = share.snapshot;
          const busy = busyId === share.shareId;
          return (
            <Card key={share.shareId} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.9rem' }}>
                    {initials(share.ownerDisplayName) || '?'}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                      {share.ownerDisplayName} shared a dive
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {share.diveLabel}
                    </Typography>
                  </Box>
                  {share.alreadyLogged && (
                    <Tooltip title="This dive is already in your log">
                      <Chip size="small" color="info" label="Already logged" />
                    </Tooltip>
                  )}
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    icon={<Straighten fontSize="small" />}
                    label={`Max ${formatDepth(s.maxDepth, units.depth)}`}
                  />
                  <Chip
                    size="small"
                    icon={<Schedule fontSize="small" />}
                    label={durationLabel(s.duration)}
                  />
                  {s.waterTemperature > 0 && (
                    <Chip
                      size="small"
                      icon={<Thermostat fontSize="small" />}
                      label={formatTemperature(s.waterTemperature, units.temperature)}
                    />
                  )}
                  {s.location && (
                    <Chip size="small" icon={<Place fontSize="small" />} label={s.location} />
                  )}
                  {s.diveProfile?.length > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`Depth profile · ${s.diveProfile.length} points`}
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
                  <Typography variant="caption" color="text.secondary">
                    Accepting adds this to your log as your own dive. You'll add your air,
                    weights and gear — those don't transfer.
                  </Typography>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<Close fontSize="small" />}
                  onClick={() => handleDecline(share)}
                  disabled={busy}
                >
                  Decline
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Check fontSize="small" />}
                  onClick={() => handleAccept(share)}
                  disabled={busy}
                >
                  Add to my log
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default IncomingSharesPanel;
